import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';
import { X } from '@phosphor-icons/react';

import { ApiError } from '@/api/client';
import {
  inviteAdministrator,
  updateAdministrator,
  type Administrator,
} from '@/api/admins';
import { listMembers } from '@/api/members';
import { listTeams, type Team } from '@/api/teams';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SearchInput } from '@/components/ui/search-input';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';

// AdminFormModal handles three flows:
//   - Invite by email (tab "email")
//   - Promote an existing member (tab "member")
//   - Edit an existing administrator (no tabs; pre-filled, role/teams editable)
// The unified backend service returns either an active membership or an
// invitation depending on whether the email matches a known core.user; the
// SPA renders both via the same toAdminItem path.
export interface AdminFormModalProps {
  open: boolean;
  onClose: () => void;
  slug: string;
  // When set, modal is in edit mode for this row.
  edit?: Administrator;
}

type Tab = 'member' | 'email';

export function AdminFormModal({ open, onClose, slug, edit }: AdminFormModalProps) {
  const { t } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDialogElement>(null);

  const isEdit = !!edit;
  const [tab, setTab] = useState<Tab>('member');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'administrator' | 'leader'>('administrator');
  const [teamIDs, setTeamIDs] = useState<string[]>([]);
  const [memberSearch, setMemberSearch] = useState('');
  const [teamSearch, setTeamSearch] = useState('');
  const [errorCode, setErrorCode] = useState<string | null>(null);

  // Reset form on open. Edit mode prefills role + teamIDs from `edit`; create
  // mode starts blank with the "From members list" tab selected.
  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    if (open && !el.open) {
      setTab('member');
      setName(edit?.display_name ?? '');
      setEmail(edit?.email ?? '');
      setRole(edit?.role === 'leader' ? 'leader' : 'administrator');
      setTeamIDs(edit?.team_ids ?? []);
      setMemberSearch('');
      setTeamSearch('');
      setErrorCode(null);
      el.showModal();
    } else if (!open && el.open) {
      el.close();
    }
  }, [open, edit]);

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    const handleClose = () => onClose();
    el.addEventListener('close', handleClose);
    return () => el.removeEventListener('close', handleClose);
  }, [onClose]);

  // Teams list — we always need it for the leader checkbox grid.
  const teamsQuery = useQuery({
    queryKey: ['teams', slug],
    queryFn: () => listTeams(token, slug),
    enabled: !!token && !!slug && open,
  });
  const teams: Team[] = useMemo(
    () => teamsQuery.data?.teams ?? [],
    [teamsQuery.data],
  );

  // Members list — only loaded when the "From members list" tab is active.
  const membersQuery = useQuery({
    queryKey: ['members', slug, memberSearch],
    queryFn: () => listMembers(token, slug, { search: memberSearch, limit: 50 }),
    enabled: !!token && !!slug && open && !isEdit && tab === 'member',
  });
  const members = membersQuery.data?.members ?? [];

  const mutation = useMutation({
    mutationFn: async () => {
      const trimmedName = name.trim();
      const trimmedEmail = email.trim();
      const payload = {
        role,
        team_ids: role === 'leader' ? teamIDs : [],
      };
      if (isEdit && edit?.user_id) {
        return updateAdministrator(token, slug, edit.user_id, payload);
      }
      return inviteAdministrator(token, slug, {
        email: trimmedEmail,
        display_name: trimmedName,
        ...payload,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['administrators', slug] });
      onClose();
    },
    onError: (err) => {
      setErrorCode(err instanceof ApiError ? err.code : 'unknown');
    },
  });

  // Selected-then-matching ordering: checked teams sort to the top so they
  // never disappear when the user types into the filter, and unselected
  // matches follow alphabetically.
  const visibleTeams = useMemo(() => {
    const q = teamSearch.trim().toLowerCase();
    const matchesSearch = (team: Team) =>
      q === '' || team.name.toLowerCase().includes(q);
    const checked = teams.filter((team) => teamIDs.includes(team.id));
    const unchecked = teams
      .filter((team) => !teamIDs.includes(team.id) && matchesSearch(team));
    return [...checked, ...unchecked];
  }, [teams, teamIDs, teamSearch]);

  const teamsValid = role === 'administrator' || teamIDs.length > 0;
  const formValid = isEdit
    ? teamsValid
    : name.trim().length > 0 && email.trim().length > 0 && teamsValid;

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!formValid || mutation.isPending) return;
    setErrorCode(null);
    mutation.mutate();
  };

  // Picking a member from the list fills name + email and switches into
  // "fields are read-only" mode visually — the user reviews and confirms.
  const pickMember = (m: { first_name: string; last_name: string; email: string }) => {
    if (!m.email) {
      setErrorCode('email_missing');
      return;
    }
    setName(`${m.first_name} ${m.last_name}`.trim());
    setEmail(m.email);
    setErrorCode(null);
    setTab('email'); // jump to the same field set so the user can hit submit
  };

  const titleKey = isEdit
    ? 'settings.administrators.modal.editTitle'
    : 'settings.administrators.modal.title';
  const submitKey = isEdit
    ? 'settings.administrators.modal.save'
    : 'settings.administrators.modal.create';
  const submittingKey = 'settings.administrators.modal.saving';

  return (
    <dialog
      ref={dialogRef}
      className="rounded-2xl border border-border bg-background p-0 shadow-2xl backdrop:bg-foreground/40"
    >
      <form
        onSubmit={onSubmit}
        noValidate
        className="flex w-[min(36rem,92vw)] flex-col gap-4 p-6"
      >
        <header className="flex items-start justify-between gap-3">
          <h2 className="font-heading text-xl font-bold text-foreground">{t(titleKey)}</h2>
          <button
            type="button"
            aria-label={t('common.cancel')}
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-pill text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </header>

        {!isEdit && (
          <div className="flex gap-1 rounded-pill bg-muted p-1 text-sm">
            <TabButton active={tab === 'member'} onClick={() => setTab('member')}>
              {t('settings.administrators.modal.tabs.member')}
            </TabButton>
            <TabButton active={tab === 'email'} onClick={() => setTab('email')}>
              {t('settings.administrators.modal.tabs.email')}
            </TabButton>
          </div>
        )}

        {!isEdit && tab === 'member' && (
          <MemberPicker
            search={memberSearch}
            onSearchChange={setMemberSearch}
            members={members}
            loading={membersQuery.isLoading}
            onPick={pickMember}
          />
        )}

        {(isEdit || tab === 'email') && (
          <>
            <label className="flex flex-col gap-1.5 text-sm font-medium text-foreground">
              {t('settings.administrators.modal.fields.name')}
              <Input
                type="text"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setErrorCode(null);
                }}
                required={!isEdit}
                disabled={isEdit}
                maxLength={200}
                autoFocus={!isEdit}
                autoComplete="name"
                placeholder={t('settings.administrators.modal.fields.namePlaceholder')}
              />
            </label>

            <label className="flex flex-col gap-1.5 text-sm font-medium text-foreground">
              {t('settings.administrators.modal.fields.email')}
              <Input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setErrorCode(null);
                }}
                required={!isEdit}
                disabled={isEdit}
                maxLength={320}
                autoComplete="email"
                placeholder={t('settings.administrators.modal.fields.emailPlaceholder')}
              />
            </label>
          </>
        )}

        <fieldset className="flex flex-col gap-2 rounded-xl border border-border bg-muted/20 p-3">
          <legend className="px-1 text-sm font-medium text-foreground">
            {t('settings.administrators.modal.fields.role')}
          </legend>
          <div className="flex flex-col gap-2 sm:flex-row">
            <RoleRadio
              value="administrator"
              current={role}
              onSelect={() => setRole('administrator')}
              label={t('role.administrator')}
            />
            <RoleRadio
              value="leader"
              current={role}
              onSelect={() => setRole('leader')}
              label={t('role.leader')}
            />
          </div>
        </fieldset>

        {role === 'leader' && (
          <fieldset className="flex flex-col gap-2 rounded-xl border border-border bg-muted/20 p-3">
            <legend className="px-1 text-sm font-medium text-foreground">
              {t('settings.administrators.modal.fields.teams')}
            </legend>
            <p className="text-xs text-muted-foreground">
              {t('settings.administrators.modal.fields.teamsHint')}
            </p>
            {teams.length > 0 && (
              <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                <SearchInput
                  wrapperClassName="flex-1"
                  value={teamSearch}
                  onChange={(e) => setTeamSearch(e.target.value)}
                  placeholder={t(
                    'settings.administrators.modal.fields.teamsSearchPlaceholder',
                  )}
                  clearLabel={t('common.clearSearch')}
                />
                <span className="text-xs text-muted-foreground sm:ml-3">
                  {t('settings.administrators.modal.fields.teamsSelectedCount', {
                    count: teamIDs.length,
                  })}
                </span>
              </div>
            )}
            <div className="flex max-h-64 flex-col gap-1.5 overflow-y-auto">
              {teams.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  {t('settings.administrators.leader.noTeams')}
                </p>
              )}
              {teams.length > 0 && visibleTeams.length === 0 && (
                <p className="px-2 py-1.5 text-xs text-muted-foreground">
                  {t('settings.administrators.modal.fields.teamsSearchEmpty')}
                </p>
              )}
              {visibleTeams.map((team) => {
                const checked = teamIDs.includes(team.id);
                return (
                  <label
                    key={team.id}
                    className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-muted/50"
                  >
                    <Checkbox
                      checked={checked}
                      onChange={(e) => {
                        if (e.target.checked) setTeamIDs([...teamIDs, team.id]);
                        else setTeamIDs(teamIDs.filter((id) => id !== team.id));
                      }}
                    />
                    <span className="text-sm text-foreground">{team.name}</span>
                  </label>
                );
              })}
            </div>
            {teamIDs.length === 0 && teams.length > 0 && (
              <p className="text-xs text-destructive">
                {t('settings.administrators.modal.fields.teamsRequired')}
              </p>
            )}
          </fieldset>
        )}

        {errorCode && (
          <p role="alert" className="text-sm text-destructive">
            {t(`settings.administrators.modal.error.${errorCode}`, {
              defaultValue: t('settings.administrators.modal.error.unknown'),
            })}
          </p>
        )}

        <footer className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={mutation.isPending}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={!formValid || mutation.isPending}>
            {mutation.isPending ? t(submittingKey) : t(submitKey)}
          </Button>
        </footer>
      </form>
    </dialog>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex-1 rounded-pill px-3 py-1.5 text-sm font-semibold transition-colors',
        active
          ? 'bg-background text-foreground shadow-sm'
          : 'text-muted-foreground hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

function RoleRadio({
  value,
  current,
  onSelect,
  label,
}: {
  value: 'administrator' | 'leader';
  current: 'administrator' | 'leader';
  onSelect: () => void;
  label: string;
}) {
  const active = value === current;
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'flex-1 rounded-pill border px-4 py-2 text-sm font-semibold transition-colors',
        active
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-border bg-background text-foreground hover:border-foreground/30',
      )}
    >
      {label}
    </button>
  );
}

function MemberPicker({
  search,
  onSearchChange,
  members,
  loading,
  onPick,
}: {
  search: string;
  onSearchChange: (v: string) => void;
  members: Array<{
    id: string;
    first_name: string;
    last_name: string;
    email: string;
  }>;
  loading: boolean;
  onPick: (m: { first_name: string; last_name: string; email: string }) => void;
}) {
  const { t } = useTranslation('common');
  return (
    <div className="flex flex-col gap-2">
      <label className="flex flex-col gap-1.5 text-sm font-medium text-foreground">
        {t('settings.administrators.modal.fields.memberSearch')}
        <SearchInput
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={t('settings.administrators.modal.fields.memberSearchPlaceholder')}
          clearLabel={t('common.clearSearch')}
          autoFocus
        />
      </label>
      <div className="max-h-64 overflow-y-auto rounded-xl border border-border">
        {loading && (
          <p className="px-3 py-2 text-sm text-muted-foreground">{t('common.loading')}</p>
        )}
        {!loading && members.length === 0 && (
          <p className="px-3 py-2 text-sm text-muted-foreground">
            {t('settings.administrators.modal.memberEmpty')}
          </p>
        )}
        {members.map((m) => (
          <button
            key={m.id}
            type="button"
            onClick={() => onPick(m)}
            className="flex w-full items-center justify-between gap-3 border-b border-border px-3 py-2 text-left last:border-b-0 hover:bg-muted/50"
          >
            <span className="flex flex-col">
              <span className="text-sm font-medium text-foreground">
                {`${m.first_name} ${m.last_name}`.trim()}
              </span>
              <span className="text-xs text-muted-foreground">
                {m.email || t('settings.administrators.modal.memberEmailMissing')}
              </span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
